/* storybook-check-ignore */
import { FunctionComponent } from 'react';
import moment from 'moment-timezone';
import { Box, Text } from '@opendoor/bricks/core';
import { ColorScheme } from '@opendoor/bricks/system/theme';
import { Token } from '@opendoor/bricks/system/token.types';
import { Listing, ListingPricingType } from '../../declarations/exclusives/listing';
import CheckmarkIcon from './icons/CheckmarkIcon';
import CloseIcon from './icons/CloseIcon';
import Icon from './icons/CurrencyIcon';
import { getListingPriceCents, getOriginalListingPriceCents } from 'helpers/exclusives/listing';

interface Props {
  listing: Listing;
  isExclusive?: boolean;
}

const PRICE_CENTS = {
  [ListingPricingType.EXCLUSIVE]: (listing: Listing) => getListingPriceCents(listing),
  [ListingPricingType.MLS]: (listing: Listing) => getOriginalListingPriceCents(listing) || 0,
};

const TEXT_PRICE_STYLE = {
  [ListingPricingType.EXCLUSIVE]: {
    backgroundColor: 'var(--colors-brand50)',
    backgroundImage:
      'linear-gradient(106.38deg, var(--colors-brand50) 33.66%, var(--colors-neutrals100) 115.11%)',
    backgroundSize: '100%',
    backgroundRepeat: 'repeat',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',

    color: 'transparent',
  },
  [ListingPricingType.MLS]: {
    color: 'var(--colors-neutrals70)',

    textDecoration: 'line-through',
  },
};

const TIME_INFO = {
  [ListingPricingType.EXCLUSIVE]: 'Buy it by',
  [ListingPricingType.MLS]: 'Publicly lists',
};

const TIME_VALUE = {
  [ListingPricingType.EXCLUSIVE]: (listing: Listing) => {
    const { start_time } = listing.exclusive_listing_info;
    return !start_time || moment(start_time) > moment()
      ? 'Available soon'
      : moment(listing.exclusive_listing_info.end_time).format('MMM D, YYYY');
  },
  [ListingPricingType.MLS]: (listing: Listing) => {
    const { start_time } = listing.exclusive_listing_info;
    return !start_time || moment(start_time) > moment()
      ? 'Available soon'
      : moment(listing.exclusive_listing_info.end_time).add(1, 'd').format('MMM D, YYYY');
  },
};

const PRICE_TITLE = {
  [ListingPricingType.EXCLUSIVE]: {
    label: 'Off-Market Exclusive Price',
    color: 'neutrals100',
  },
  [ListingPricingType.MLS]: {
    label: 'Future Public List Price',
    color: 'neutrals70',
  },
};

type PriceInfo = Record<ListingPricingType, { label: string; icon: FunctionComponent }>;

const APPRAISAL_GUARANTEE_INFO = {
  [ListingPricingType.EXCLUSIVE]: {
    label: 'Appraisal Price Match Guarantee',
    icon: CheckmarkIcon,
  },
  [ListingPricingType.MLS]: {
    label: 'No Appraisal Price Match Guarantee',
    icon: CloseIcon,
  },
} as PriceInfo;

const BIDDING_WAR_INFO = {
  [ListingPricingType.EXCLUSIVE]: {
    label: 'Skip the bidding wars',
    icon: CheckmarkIcon,
  },
  [ListingPricingType.MLS]: {
    label: 'Subject to bidding wars',
    icon: CloseIcon,
  },
} as PriceInfo;

const HowPricingWorksPriceSection: React.FC<Props> = ({ listing, isExclusive }) => {
  const type = isExclusive ? ListingPricingType.EXCLUSIVE : ListingPricingType.MLS;

  return (
    <Box maxWidth={[null, null, null, null, '100%']} minWidth="50%">
      <Text
        fontSize="s00"
        fontWeight="semibold"
        lineHeight="s00"
        pb="0.5rem"
        color={PRICE_TITLE[type].color as Token<ColorScheme>}
      >
        {PRICE_TITLE[type].label}
      </Text>
      <Text
        as="div"
        pr="0.5rem"
        fontSize="s4"
        lineHeight="s4"
        fontWeight="bold"
        backgroundClip="text"
        style={TEXT_PRICE_STYLE[type]}
      >
        ${(PRICE_CENTS[type](listing) / 100).toLocaleString()}
        {isExclusive && (
          <Box display="inline" ml="1rem">
            <Icon size="1.5rem" />
          </Box>
        )}
      </Text>
      <Box my="1.5rem">
        <Text fontSize="s00" lineHeight="s00">
          {TIME_INFO[type]}
        </Text>
        <Text fontSize="s0" lineHeight="s0" fontWeight="semibold">
          {TIME_VALUE[type](listing)}
        </Text>
      </Box>
      <Box mb="1rem">
        {[APPRAISAL_GUARANTEE_INFO, BIDDING_WAR_INFO].map((InfoObject, idx) => (
          <Box key={`price-info-${idx}`} flexDirection="row" mb="0.5rem" display="flex">
            <Box>{InfoObject[type].icon({})}</Box>
            <Text fontSize="s0" lineHeight="s0" ml="0.5rem">
              {InfoObject[type].label}
            </Text>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default HowPricingWorksPriceSection;
